import { PUBLIC_CONVEX_SITE_URL, readEnv } from '../env'
import { ACQUISITION_TOKEN, ACQUISITION_TTL_MS, campaignAttribution, isProductionMarketingHost, safeMarketingUrl, type AcquisitionEvent, type CampaignAttribution } from './acquisition-policy'

const CONSENT_KEY = 'kindlumen.analytics-consent.v1'
const VISIT_KEY = 'kindlumen.acquisition.v1'
export const ANALYTICS_CHANGE = 'kindlumen:analytics-consent'
type Visit = { token: string; createdAt: number; attribution: CampaignAttribution }
type Pixel = ((...args: unknown[]) => void) & { queue?: unknown[][]; callMethod?: (...args: unknown[]) => void; push?: Pixel; loaded?: boolean; version?: string; disablePushState?: boolean }
type AnalyticsWindow = Window & { fbq?: Pixel; _fbq?: Pixel }
const pending = new Map<string, Promise<void>>()
const recorded = new Set<string>()
const controllers = new Set<AbortController>()
let pixelStarted = false
let pixelReady = false
let lastPage = ''
let excluded = true
let entryAttribution: CampaignAttribution | null = null

export function analyticsConsent(): 'accepted' | 'declined' | null {
  if (typeof window === 'undefined') return null
  try { const value = localStorage.getItem(CONSENT_KEY); return value === 'accepted' || value === 'declined' ? value : null } catch { return 'declined' }
}

export function setAnalyticsConsent(value: 'accepted' | 'declined' | null) {
  try {
    if (value) localStorage.setItem(CONSENT_KEY, value)
    else localStorage.removeItem(CONSENT_KEY)
    if (value !== 'accepted') { localStorage.removeItem(VISIT_KEY); recorded.clear(); entryAttribution = null }
  } catch { /* Storage restrictions must not block the site. */ }
  if (value !== 'accepted') {
    for (const controller of controllers) controller.abort()
    ;(window as AnalyticsWindow).fbq?.('consent', 'revoke')
  }
  window.dispatchEvent(new Event(ANALYTICS_CHANGE))
}

function canTrack() {
  return typeof window !== 'undefined' && !excluded && analyticsConsent() === 'accepted' &&
    isProductionMarketingHost(window.location.hostname) && safeMarketingUrl(new URL(window.location.href)) && window.parent === window
}

function currentVisit(): Visit | null {
  if (!canTrack()) return null
  try {
    const stored = JSON.parse(localStorage.getItem(VISIT_KEY) || 'null') as Visit | null
    if (stored && ACQUISITION_TOKEN.test(stored.token) && stored.createdAt <= Date.now() && Date.now() - stored.createdAt < ACQUISITION_TTL_MS) return stored
    const visit: Visit = {
      token: Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join(''),
      createdAt: Date.now(), attribution: entryAttribution ?? campaignAttribution(window.location.search),
    }
    localStorage.setItem(VISIT_KEY, JSON.stringify(visit))
    return visit
  } catch { return null }
}

export async function trackAcquisition(event: AcquisitionEvent): Promise<void> {
  const visit = currentVisit()
  if (!visit || !PUBLIC_CONVEX_SITE_URL) return
  const key = `${visit.token}:${event}`
  if (recorded.has(key)) return
  if (pending.has(key)) return pending.get(key)
  const controller = new AbortController()
  controllers.add(controller)
  const timeout = setTimeout(() => controller.abort(), 2500)
  const request = fetch(`${PUBLIC_CONVEX_SITE_URL}/public/acquisition`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ token: visit.token, event, search: new URLSearchParams(visit.attribution).toString(), consent: true }),
    signal: controller.signal,
  }).then(response => { if (response.ok) recorded.add(key) }).catch(() => undefined).finally(() => { pending.delete(key); controllers.delete(controller); clearTimeout(timeout) })
  pending.set(key, request)
  return request
}

// Await the idempotent intake before auth so the server's success hook can bind
// this anonymous visit. A blocked tracker simply yields an unattributed signup.
export async function acquisitionSignupHeaders(): Promise<Record<string, string>> {
  await trackAcquisition('signup_started')
  const visit = currentVisit()
  return visit && recorded.has(`${visit.token}:signup_started`) ? { 'x-kindlumen-acquisition': visit.token } : {}
}

function loadPixel() {
  const pixelId = readEnv('VITE_META_PIXEL_ID')
  if (!pixelId || !/^\d{5,25}$/.test(pixelId) || !canTrack()) return null
  const target = window as AnalyticsWindow
  if (!pixelStarted) {
    pixelStarted = true
    const pixel: Pixel = (...args) => { if (pixel.callMethod) pixel.callMethod(...args); else pixel.queue!.push(args) }
    pixel.queue = []; pixel.push = pixel; pixel.loaded = true; pixel.version = '2.0'; pixel.disablePushState = true
    target.fbq = pixel; target._fbq = pixel
    pixel('set', 'autoConfig', false, pixelId)
    pixel('init', pixelId)
    const script = document.createElement('script')
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'; script.async = true
    // Never queue page events while the SDK downloads: navigation could change
    // the document URL before Meta reads it. Recheck the boundary on load.
    script.onload = () => { pixelReady = true; updateMarketingAnalytics(excluded) }
    document.head.append(script)
  }
  if (!pixelReady) return null
  target.fbq?.('consent', 'grant')
  return { pixel: target.fbq!, pixelId }
}

export function trackRegistration() {
  if (!canTrack()) return
  const visit = currentVisit()
  if (!visit) return
  const key = `registered:${visit.token}`
  if (recorded.has(key)) return
  const meta = loadPixel()
  if (meta) {
    meta.pixel('trackSingle', meta.pixelId, 'CompleteRegistration', { content_name: 'KindLumen organization account', status: true }, { eventID: key })
    recorded.add(key)
  }
}

export function updateMarketingAnalytics(isExcluded = false) {
  excluded = isExcluded
  // Keep campaign labels in memory across SPA navigation while consent is
  // undecided. No identifier, storage or network activity starts here.
  if (!excluded && !entryAttribution && analyticsConsent() !== 'declined' &&
      isProductionMarketingHost(window.location.hostname) && safeMarketingUrl(new URL(window.location.href))) {
    entryAttribution = campaignAttribution(window.location.search)
  }
  const target = window as AnalyticsWindow
  if (!canTrack()) {
    target.fbq?.('consent', 'revoke')
    lastPage = ''
    return
  }
  void trackAcquisition('landing')
  const meta = loadPixel()
  const path = window.location.pathname
  if (meta && lastPage !== path) {
    meta.pixel('trackSingle', meta.pixelId, 'PageView')
    lastPage = path
  }
}
